
import React, { useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { 
  Form, 
  FormControl, 
  FormField, 
  FormItem, 
  FormLabel, 
  FormMessage 
} from '@/components/ui/form';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { PaymentMethod, PaymentInfo } from '@/types/expense';
import { toast } from 'sonner';

const paymentMethodSchema = z.object({
  method: z.enum(['venmo', 'zelle', 'cashapp', 'other']),
  handle: z.string().min(3, { message: 'Username/handle must be at least 3 characters.' }),
});

interface PaymentInfoFormProps {
  userId: string;
}

const PaymentInfoForm: React.FC<PaymentInfoFormProps> = ({ userId }) => {
  const [savedInfo, setSavedInfo] = useState<PaymentInfo | null>(null);
  
  const form = useForm<z.infer<typeof paymentMethodSchema>>({
    resolver: zodResolver(paymentMethodSchema),
    defaultValues: {
      method: 'venmo',
      handle: '',
    },
  });

  // Load saved payment info from localStorage on component mount
  useEffect(() => {
    const storedInfo = localStorage.getItem(`payment_info_${userId}`);
    if (storedInfo) {
      try {
        const parsedInfo = JSON.parse(storedInfo) as PaymentInfo;
        setSavedInfo(parsedInfo);
        form.reset({
          method: parsedInfo.method,
          handle: parsedInfo.handle,
        });
      } catch (error) {
        console.error('Failed to parse stored payment info:', error);
      }
    }
  }, [userId, form]);

  const onSubmit = (values: z.infer<typeof paymentMethodSchema>) => {
    const paymentInfo: PaymentInfo = {
      userId,
      method: values.method as PaymentMethod,
      handle: values.handle,
    };
    
    // Save to localStorage
    localStorage.setItem(`payment_info_${userId}`, JSON.stringify(paymentInfo));
    setSavedInfo(paymentInfo);
    toast.success('Payment information saved');
  };

  const getMethodLabel = (method: PaymentMethod) => {
    switch (method) {
      case 'venmo': return 'Venmo';
      case 'zelle': return 'Zelle';
      case 'cashapp': return 'Cash App';
      case 'other': return 'Other';
      default: return method;
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Payment Information</h3>
      
      {savedInfo && (
        <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
          <p className="text-sm text-gray-700">
            <span className="font-medium">{getMethodLabel(savedInfo.method)}:</span> {savedInfo.handle}
          </p>
        </div>
      )}
      
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="method"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Payment Method</FormLabel>
                  <Select 
                    onValueChange={field.onChange} 
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select payment method" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="venmo">Venmo</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="cashapp">Cash App</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            
            <FormField
              control={form.control}
              name="handle"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Username/Handle</FormLabel>
                  <FormControl>
                    <Input placeholder="@username" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          
          <Button 
            type="submit" 
            variant="outline" 
            className="w-full sm:w-auto"
          >
            Save Payment Info
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default PaymentInfoForm;
